import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

export const RerunAutomationsModal = memo(function RerunAutomationsModal({
  isOpen,
  onClose,
  onConfirm,
  isAllUnfulfilled,
  numAutomations,
  numUnfulfilledGroups,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isAllUnfulfilled: boolean;
  numAutomations: number | undefined;
  numUnfulfilledGroups: number | undefined;
}) {
  const { t } = useTranslation();
  const [automationsTriggered, setAutomationsTriggered] = useState(false);
  const handleRerunAutomations = useHandler(async () => {
    setAutomationsTriggered(true);
    await onConfirm();
    setAutomationsTriggered(false);
    onClose();
  });

  const primaryButton: RedoModalButtonProps = {
    text: t("Rerun Automations"),
    onClick: handleRerunAutomations,
    pending: automationsTriggered,
  };

  const secondaryButton: RedoModalButtonProps = {
    text: t("Cancel"),
    onClick: onClose,
    disabled: automationsTriggered,
    pending: false,
  };

  const automationMessage = numAutomations
    ? t("This will rerun all of your automations ({{count}}).", {
        count: numAutomations,
      })
    : t("This will rerun all of your automations.");
  const orderMessage = (() => {
    if (isAllUnfulfilled) {
      if (numUnfulfilledGroups === undefined) {
        return t("It will run on all of your unfulfilled orders.");
      }
      return t("It will run on all of your unfulfilled orders ({{count}}).", {
        count: numUnfulfilledGroups,
      });
    }
    if (numUnfulfilledGroups === undefined) {
      return t("It will run on your selected orders.");
    }
    return t("It will run on your selected orders ({{count}}).", {
      count: numUnfulfilledGroups,
    });
  })();
  const processMessage = t(
    "This process may refresh rates and take a few minutes to complete.",
  );

  return (
    <RedoModal
      buttonPlacement="tight"
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      title={t("Rerun all automations")}
    >
      <Flex dir="column" gap="xl">
        <Text fontSize="sm" textColor="primary">
          {automationMessage} {orderMessage} {processMessage}
        </Text>
      </Flex>
    </RedoModal>
  );
});
